import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AddressAPIService } from '@vendasta/address';
import { Address } from '@vendasta/businesses';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { getCode as getCountryCodeFromName, getName as getCountryNameFromCode } from 'country-list';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { DeleteImageModalComponent } from './delete-image-modal/delete-image-modal.component';
import { UploadImageModalComponent } from './upload-image-modal/upload-image-modal.component';
import { DEFAULT_IMAGE_URL, PlainPhoneNumber, UserProfileService } from './user-profile.service';

@Component({
  selector: 'user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  @Input() unifiedUserId: string;

  isUserEditing = false;
  isUpdatingPassword = false;
  disabledRemoveImg = false;
  canAddMorePhones = true;

  userProfileFormGroup$: Observable<UntypedFormGroup>;
  updatePasswordFormGroup = new UntypedFormGroup({
    current_password: new UntypedFormControl(),
    new_password: new UntypedFormControl(),
  });

  countryOptions$: Observable<SelectInputOptionInterface[]>;
  stateProvOptions$$ = new BehaviorSubject<SelectInputOptionInterface[]>([]);
  private readonly onDestroy = new Subject<void>();

  constructor(
    private userProfileService: UserProfileService,
    private snackbarService: SnackbarService,
    public dialog: MatDialog,
    private readonly addressService: AddressAPIService,
  ) {}

  ngOnInit(): void {
    this.userProfileFormGroup$ = this.userProfileService.getUser(this.unifiedUserId).pipe(
      take(1),
      map((user) => {
        const profileImage = user.profileImageUrl || DEFAULT_IMAGE_URL;
        this.disabledRemoveImg = profileImage === DEFAULT_IMAGE_URL;

        const phoneFormGroups =
          user.phoneNumbers.map((n) => {
            const mappedPhoneNumber = this.mapRFC3966FormatToPlainPhoneNumber(n.phoneNumber);
            return this.createPhoneNumberForm(mappedPhoneNumber);
          }) || [];
        if (phoneFormGroups.length >= 3) {
          this.canAddMorePhones = false;
        }

        const formGroup = new UntypedFormGroup({
          first_name: new UntypedFormControl(user.firstName),
          last_name: new UntypedFormControl(user.lastName),
          email: new UntypedFormControl({ value: user.email, disabled: true }),
          profile_image: new UntypedFormControl(profileImage),
          phone_numbers: new UntypedFormArray(phoneFormGroups),
          country: new UntypedFormControl(this.validateCountryCode(user.address.country)),
          address: new UntypedFormControl(user.address.address || ''),
          additionalAddressLine: new UntypedFormControl(user.address.additionalAddress),
          city: new UntypedFormControl(user.address.city || ''),
          postalCode: new UntypedFormControl(user.address.postalCode || ''),
        });
        this.updateStateProvWithCountry(user.address.state.split('-')[1] || '');
        return formGroup;
      }),
      catchError(() => {
        this.snackbarService.openErrorSnack('EDIT_USER_PROFILE.USER_NOT_FOUND');
        return of(null);
      }),
      publishReplay(1),
      refCount(),
    );

    this.countryOptions$ = this.addressService.listAllCountryOptions().pipe(
      take(1),
      map((countries) => {
        if (!countries) {
          return [];
        }
        return countries.sort((a, b) => {
          const nameA = a.name.toUpperCase();
          const nameB = b.name.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
      }),
      map((countries) =>
        countries.map((country) => ({
          value: country?.code || '',
          label: country?.name || '',
        })),
      ),
      publishReplay(1),
      refCount(),
    );
  }

  ngOnDestroy(): void {
    this.onDestroy.complete();
  }

  private updateStateProvWithCountry(state: string): void {
    this.userProfileFormGroup$
      .pipe(
        switchMap((fg) => fg.controls.country.valueChanges),
        takeUntil(this.onDestroy),
        filter((country) => !!country),
        distinctUntilChanged(),
        tap((country) => this.setCountry(country, state)),
      )
      .subscribe();
  }

  private setCountry(country: string, state: string): void {
    this.addressService
      .getCountryConfiguration(country, undefined)
      .pipe(
        map((countryConfig) =>
          countryConfig?.zones?.map((zone) => ({
            value: zone?.code || '',
            label: zone?.name || '',
          })),
        ),
        catchError(() => []),
        tap((stateProvs: SelectInputOptionInterface[]) => this.stateProvOptions$$.next(stateProvs)),
        withLatestFrom(this.userProfileFormGroup$),
        tap(([stateProvs, form]) => {
          if (!!stateProvs && stateProvs.findIndex((sp) => sp.value === state) > 0) {
            form.setControl('stateProv', new UntypedFormControl(state));
          } else {
            form.setControl('stateProv', new UntypedFormControl(null));
          }
        }),
        take(1),
      )
      .subscribe();
  }

  private validateCountryCode(countryKey: string): string {
    if (!countryKey) {
      return '';
    }

    const countryName = getCountryNameFromCode(countryKey);
    if (countryName) {
      return countryKey;
    }

    const countryCode = getCountryCodeFromName(countryKey);
    if (countryCode) {
      return countryCode;
    }
    return '';
  }

  mapRFC3966FormatToPlainPhoneNumber(rfc3966Format: string): PlainPhoneNumber {
    const numberSections = rfc3966Format.split(';ext=');
    return { phone: numberSections[0].substring(5).replace('-', ''), ext: numberSections[1] } as PlainPhoneNumber;
  }

  mapPlainToRFC3966FormatPhoneNumber(formArray: UntypedFormArray): string[] {
    return formArray.value.map((fg) => {
      if (fg.phone !== '') {
        if (fg.ext) {
          return '+' + fg.phone + ';ext=' + fg.ext;
        } else {
          return '+' + fg.phone;
        }
      }
    });
  }

  addPhoneNumber(userForm: UntypedFormGroup): void {
    const phonesFormArray = <UntypedFormArray>userForm.controls.phone_numbers;
    phonesFormArray.push(this.createPhoneNumberForm());
    if (phonesFormArray.controls.length >= 3) {
      this.canAddMorePhones = false;
    }
  }

  createPhoneNumberForm(plainNumber?: PlainPhoneNumber): UntypedFormGroup {
    const form = new UntypedFormGroup({
      phone: new UntypedFormControl(),
      ext: new UntypedFormControl(),
    });
    if (plainNumber) {
      form.get('phone').setValue(plainNumber.phone);
      form.get('ext').setValue(plainNumber.ext);
    }
    return form;
  }

  deletePhoneNumber(userForm: UntypedFormGroup, index: number): void {
    const phonesFormArray = <UntypedFormArray>userForm.controls.phone_numbers;
    phonesFormArray.removeAt(index);
    if (phonesFormArray.controls.length < 3) {
      this.canAddMorePhones = true;
    }
  }

  saveUserProfile(userForm: UntypedFormGroup): void {
    const phoneFormArray = <UntypedFormArray>userForm.controls.phone_numbers;
    if (!userForm.valid) {
      this.snackbarService.openErrorSnack('EDIT_USER_PROFILE.INVALID_FORM');
      return;
    }

    const phones = this.mapPlainToRFC3966FormatPhoneNumber(phoneFormArray);
    const firstName = userForm.controls.first_name.value;
    const lastName = userForm.controls.last_name.value;

    const address = {
      address1: userForm.controls.address.value,
      address2: userForm.controls.additionalAddressLine.value,
      city: userForm.controls.city.value,
      province: userForm.controls.stateProv.value || '',
      country: userForm.controls.country.value,
      zip: userForm.controls.postalCode.value,
    } as Address;

    this.userProfileService
      .updateUser(this.unifiedUserId, firstName, lastName, address, phones)
      .pipe(take(1))
      .subscribe(
        () => {
          this.isUserEditing = false;
          this.snackbarService.openSuccessSnack('EDIT_USER_PROFILE.SUCCESS');
        },
        () => {
          this.isUserEditing = false;
          this.snackbarService.openErrorSnack('EDIT_USER_PROFILE.FAILURE');
        },
      );
  }

  uploadPhotoModal(userForm: UntypedFormGroup): void {
    this.disabledRemoveImg = false;
    this.dialog
      .open(UploadImageModalComponent, {
        width: '650px',
      } as MatDialogConfig)
      .afterClosed()
      .pipe(
        switchMap((response) => {
          if (response && response.url) {
            const profileImageControl = userForm.controls.profile_image;
            profileImageControl.setValue(response.url);
            return this.userProfileService.updateUserImage(this.unifiedUserId, profileImageControl.value);
          }
        }),
        take(1),
      )
      .subscribe((success) => {
        if (success) {
          this.disabledRemoveImg = false;
          this.snackbarService.openSuccessSnack('PHOTO.UPLOAD_SUCCESS');
        } else {
          this.snackbarService.openErrorSnack('PHOTO.UPLOAD_ERROR');
        }
      });
  }

  removeImage(userForm: UntypedFormGroup, isDisabled: boolean): void {
    if (!isDisabled) {
      this.dialog
        .open(DeleteImageModalComponent)
        .afterClosed()
        .pipe(
          switchMap((response) => {
            if (response.continue) {
              const profileImageControl = userForm.controls.profile_image;
              profileImageControl.setValue(DEFAULT_IMAGE_URL);
              return this.userProfileService.updateUserImage(this.unifiedUserId, profileImageControl.value);
            }
          }),
          take(1),
        )
        .subscribe((success) => {
          if (success) {
            this.disabledRemoveImg = true;
            this.snackbarService.openSuccessSnack('PHOTO.UPLOAD_SUCCESS');
          } else {
            this.snackbarService.openErrorSnack('PHOTO.UPLOAD_ERROR');
          }
        });
    }
  }

  updatePassword(): void {
    if (this.updatePasswordFormGroup.invalid) {
      this.snackbarService.openErrorSnack('PASSWORD.INVALID_FORM');
      return;
    }
    this.isUpdatingPassword = true;

    const currentPassword = this.updatePasswordFormGroup.value.current_password;
    const newPassword = this.updatePasswordFormGroup.value.new_password;
    this.userProfileService
      .updatePassword(this.unifiedUserId, currentPassword, newPassword)
      .pipe(take(1))
      .subscribe((success) => {
        this.isUpdatingPassword = false;
        if (success) {
          this.snackbarService.openSuccessSnack('PASSWORD.CHANGE_SUCCESS');
        } else {
          this.snackbarService.openErrorSnack('PASSWORD.CHANGE_ERROR');
        }
      });
  }
}
